import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import Modal from '../components/Modal';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api, apiBlob } from '../api/client';
import { money, date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = (incomeId) => ({ item_id: '', description: '', quantity: 1, unit_price: 0, taxable: true, income_account_id: incomeId || '', expense_id: null });

function addrLines(a) {
  if (!a) return [];
  const cityLine = [a.city, a.state].filter(Boolean).join(', ') + (a.postal_code ? ` ${a.postal_code}` : '');
  return [a.line1, a.line2, cityLine.trim()].filter(Boolean);
}

export default function Invoices() {
  const { activeId } = useBusiness();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [meta, setMeta] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [paying, setPaying] = useState(false);
  const [payForm, setPayForm] = useState(null);
  const [paySaving, setPaySaving] = useState(false);
  const [payError, setPayError] = useState(null);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('new'); // new | edit | view
  const [current, setCurrent] = useState(null); // loaded invoice (view/edit)
  const [form, setForm] = useState(null);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [billableOpen, setBillableOpen] = useState(false);
  const [billableList, setBillableList] = useState([]);
  const [billableSel, setBillableSel] = useState(new Set());
  const [billableLoading, setBillableLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState(null);
  const [retainer, setRetainer] = useState(null); // { collected, applied, available } for current invoice's customer
  const [applying, setApplying] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [jobsForCustomer, setJobsForCustomer] = useState([]);
  const [retainerAmt, setRetainerAmt] = useState('');

  async function downloadPdf() {
    if (!current) return;
    setPdfBusy(true); setSendMsg(null);
    try {
      const blob = await apiBlob(`/invoices/${current.id}/pdf?download=1`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${current.invoice_number || 'invoice'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { setSendMsg({ ok: false, text: 'Could not generate the PDF.' }); }
    finally { setPdfBusy(false); }
  }

  async function setInvoiceJob(jobId) {
    if (!current) return;
    setSendMsg(null);
    try {
      const inv = await api(`/invoices/${current.id}/job`, { method: 'POST', body: { job_id: jobId || null } });
      setCurrent(inv);
      setForm((f) => ({ ...f, job_id: jobId || '' }));
      loadList();
    } catch (e) {
      setSendMsg({ ok: false, text: e?.detail === 'job_mismatch' ? 'That job belongs to a different customer.' : (e?.detail || 'Could not update the job link.') });
    }
  }

  async function applyRetainerAmount() {
    if (!current || !retainer || retainer.available <= 0) return;
    const max = Math.min(retainer.available, Number(current.balance));
    let amt = retainerAmt === '' ? max : Number(retainerAmt);
    if (!(amt > 0)) { setPayError('Enter a retainer amount greater than zero.'); return; }
    amt = Math.min(amt, max);
    setApplying(true); setPayError(null);
    try {
      const r = await api(`/invoices/${current.id}/apply-retainer`, { method: 'POST', body: { amount: amt } });
      if (r.invoice) setCurrent(r.invoice);
      setRetainer((prev) => (prev ? { ...prev, available: r.available, applied: Math.round((prev.applied + r.applied) * 100) / 100 } : prev));
      setRetainerAmt('');
      loadList();
    } catch (e) {
      setPayError(e?.detail === 'no_retainer' ? 'No retainer credit available.' : (e?.detail || 'Could not apply retainer credit.'));
    } finally { setApplying(false); }
  }

  async function saveNotes() {
    if (!current) return;
    setNotesSaving(true); setSendMsg(null);
    try {
      const inv = await api(`/invoices/${current.id}/notes`, { method: 'POST', body: { terms: form.terms || '' } });
      setCurrent(inv);
      setSendMsg({ ok: true, text: 'Notes saved.' });
    } catch (e) { setSendMsg({ ok: false, text: e?.detail || 'Could not save notes.' }); }
    finally { setNotesSaving(false); }
  }

  async function applyRetainer() {
    if (!current || !retainer || retainer.available <= 0) return;
    const isDraft = current.status === 'draft';
    const amt = Math.min(retainer.available, Number(current.balance));
    const msg = isDraft
      ? `Applying will finalize ${current.invoice_number || 'this invoice'} and apply ${money(amt)} of retainer credit. Continue?`
      : `Apply ${money(amt)} of retainer credit to ${current.invoice_number}?`;
    if (!window.confirm(msg)) return;
    setApplying(true); setSendMsg(null);
    try {
      // Persist any in-progress edits first so finalize captures them.
      if (isDraft) {
        if (!form.customer_id || lines.filter((l) => l.description.trim()).length === 0) {
          setSendMsg({ ok: false, text: 'Add a customer and at least one line item before applying.' });
          setApplying(false); return;
        }
        await api(`/invoices/${current.id}`, { method: 'PUT', body: buildInvoiceBody(false) });
      }
      const r = await api(`/invoices/${current.id}/apply-retainer`, { method: 'POST', body: {} });
      if (r.invoice) { setCurrent(r.invoice); setMode('view'); }
      setRetainer((prev) => (prev ? { ...prev, available: r.available, applied: Math.round((prev.applied + r.applied) * 100) / 100 } : prev));
      setSendMsg({ ok: true, text: `Applied ${money(r.applied)} of retainer credit.` });
      loadList();
    } catch (e) {
      setSendMsg({ ok: false, text: e?.detail === 'no_retainer' ? 'This customer has no retainer credit available.' : (e?.detail || 'Could not apply retainer credit.') });
    } finally { setApplying(false); }
  }

  async function voidInvoice() {
    if (!current) return;
    if (!window.confirm(`Void invoice ${current.invoice_number}? This reverses its ledger entry and can’t be undone.`)) return;
    setSending(true); setSendMsg(null);
    try {
      const inv = await api(`/invoices/${current.id}/void`, { method: 'POST' });
      setCurrent(inv); loadList();
      setSendMsg({ ok: true, text: 'Invoice voided.' });
    } catch (e) {
      setSendMsg({ ok: false, text: e?.detail === 'has_payments' ? 'This invoice has payments applied — remove or refund them before voiding.' : (e?.detail || 'Could not void the invoice.') });
    } finally { setSending(false); }
  }

  async function sendInvoice() {
    if (!current) return;
    setSending(true); setSendMsg(null);
    try {
      const r = await api(`/invoices/${current.id}/send`, { method: 'POST' });
      if (r.emailed) {
        setSendMsg({ ok: true, text: `Sent to ${r.to}.` });
        if (r.invoice) setCurrent(r.invoice);
        loadList();
      } else if (r.reason === 'not_configured') {
        setSendMsg({ ok: false, text: 'Server email isn’t set up — use Download PDF and send it manually.' });
      } else {
        setSendMsg({ ok: false, text: `Couldn’t send (${r.reason}).` });
      }
    } catch (e) {
      setSendMsg({ ok: false, text: e?.detail === 'no_recipient' ? 'No email address on file for this customer.' : (e?.detail || 'Could not send the invoice.') });
    } finally { setSending(false); }
  }

  const loadList = useCallback(() => {
    setLoading(true);
    setError(null);
    api(`/invoices?business_id=${activeId}`)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => {
        setRows([]);
        if (e?.status && e.status !== 404) setError('Could not load invoices. Check the API connection.');
      })
      .finally(() => setLoading(false));
  }, [activeId]);

  useEffect(() => { loadList(); }, [loadList]);

  // Jobs for the chosen customer (for the link picker) — in create/edit and when viewing.
  useEffect(() => {
    const cid = form?.customer_id;
    if (!open || !cid) { setJobsForCustomer([]); return; }
    api(`/jobs?business_id=${activeId}&customer_id=${cid}`)
      .then((d) => setJobsForCustomer(Array.isArray(d) ? d : []))
      .catch(() => setJobsForCustomer([]));
  }, [open, mode, form?.customer_id, activeId]);

  // Form reference data (tax rate, income accounts, customers) per business
  useEffect(() => {
    api(`/invoices/meta?business_id=${activeId}`).then(setMeta).catch(() => setMeta(null));
    api(`/customers?business_id=${activeId}`)
      .then((data) => setCustomers((Array.isArray(data) ? data : []).filter((c) => c.is_active !== false)))
      .catch(() => setCustomers([]));
    api(`/items?business_id=${activeId}&active=1`)
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]));
    api(`/accounts?business_id=${activeId}&type=asset`)
      .then((d) => setDepositAccounts((Array.isArray(d) ? d : []).filter((a) => a.is_bank || a.code === '1010')))
      .catch(() => setDepositAccounts([]));
  }, [activeId]);

  const taxRate = meta?.collects_sales_tax ? Number(meta.tax_rate) || 0 : 0;
  const defaultIncome = meta?.default_income_account_id || '';
  const reimbursementIncomeId = meta?.income_accounts?.find((a) => a.code === '4100')?.id || defaultIncome;

  async function openBillable() {
    if (!form.customer_id) { setFormError('Choose a customer first.'); return; }
    setBillableSel(new Set()); setBillableLoading(true); setBillableOpen(true);
    try {
      const list = await api(`/invoices/billable-expenses?business_id=${activeId}&customer_id=${form.customer_id}`);
      // The endpoint already excludes expenses billed on a saved invoice. Also hide any that are already
      // on THIS invoice (added earlier this session but not yet saved) so they can't be added twice.
      const already = new Set(lines.map((l) => l.expense_id).filter(Boolean).map(String));
      setBillableList((Array.isArray(list) ? list : []).filter((e) => !already.has(String(e.id))));
    } catch { setBillableList([]); }
    finally { setBillableLoading(false); }
  }
  function toggleBillable(id) {
    const key = String(id);
    setBillableSel((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }
  function addBillable() {
    // Coerce ids to strings on both sides so selection always matches, regardless of how the API
    // serialized the expense id (bigint can come back as a string).
    const chosenIds = new Set([...billableSel].map(String));
    const chosen = billableList.filter((e) => chosenIds.has(String(e.id)));
    if (!chosen.length) {
      setSendMsg({ ok: false, text: 'Nothing was selected — tick at least one expense first.' });
      return;
    }
    setLines((ls) => [
      ...ls,
      ...chosen.map((e) => ({
        item_id: '', description: `${e.vendor || 'Expense'} (${date(e.expense_date)})`,
        quantity: 1, unit_price: Number(e.total) || 0, taxable: false,
        income_account_id: reimbursementIncomeId, expense_id: e.id,
      })),
    ]);
    setBillableOpen(false);
    setSendMsg({ ok: true, text: `Added ${chosen.length} billable expense${chosen.length === 1 ? '' : 's'} as line item${chosen.length === 1 ? '' : 's'}. Review the lines and Save.` });
  }

  const totals = useMemo(() => {
    const isRet = form?.kind === 'retainer';
    let subtotal = 0, taxable = 0;
    for (const l of lines) {
      const amt = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
      subtotal += amt;
      if (!isRet && l.taxable !== false) taxable += amt;
    }
    const tax = taxable * taxRate;
    return { subtotal, tax, total: subtotal + tax };
  }, [lines, taxRate, form]);

  function openNew(kind = 'standard') {
    setMode('new');
    setCurrent(null);
    setSendMsg(null);
    setRetainer(null);
    setForm({ customer_id: '', issue_date: today(), due_date: '', terms: '', kind, job_id: '' });
    setLines([emptyLine(defaultIncome)]);
    setFormError(null);
    setOpen(true);
  }

  async function openRow(row) {
    setFormError(null);
    setPaying(false);
    setSendMsg(null);
    setOpen(true);
    try {
      const inv = await api(`/invoices/${row.id}`);
      setCurrent(inv);
      setMode(inv.status === 'draft' ? 'edit' : 'view');
      setForm({
        customer_id: inv.customer_id,
        issue_date: inv.issue_date?.slice(0, 10) || today(),
        due_date: inv.due_date?.slice(0, 10) || '',
        terms: inv.terms || '',
        kind: inv.kind || 'standard',
        job_id: inv.job_id || '',
      });
      setRetainer(null);
      if ((inv.kind || 'standard') === 'standard' && ['draft', 'open', 'partial', 'overdue'].includes(inv.status)) {
        api(`/invoices/retainer-balance?business_id=${activeId}&customer_id=${inv.customer_id}`)
          .then((d) => setRetainer(d)).catch(() => setRetainer(null));
      }
      setLines(
        (inv.lines || []).map((l) => ({
          description: l.description, quantity: Number(l.quantity), unit_price: Number(l.unit_price),
          taxable: l.taxable, income_account_id: l.income_account_id, item_id: l.item_id || '', expense_id: l.expense_id || null,
        }))
      );
    } catch {
      setOpen(false);
    }
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine(defaultIncome)]);

  // Picking a catalog item fills the line; description stays editable for overrides.
  function selectItem(i, itemId) {
    const it = items.find((x) => String(x.id) === String(itemId));
    setLines((ls) => ls.map((l, idx) => {
      if (idx !== i) return l;
      if (!it) return { ...l, item_id: '' };
      return {
        ...l,
        item_id: it.id,
        description: it.description || it.name,
        unit_price: Number(it.unit_price) || 0,
        taxable: it.taxable !== false,
        income_account_id: it.income_account_id || defaultIncome,
      };
    }));
  }
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  function buildInvoiceBody(finalize) {
    const isRet = form?.kind === 'retainer';
    const clean = lines.filter((l) => l.description.trim());
    return {
      business_id: activeId,
      customer_id: Number(form.customer_id),
      issue_date: form.issue_date,
      due_date: form.due_date || null,
      terms: form.terms || null,
      status: finalize ? 'open' : 'draft',
      kind: isRet ? 'retainer' : 'standard',
      job_id: form.job_id || null,
      lines: clean.map((l) => ({
        description: l.description.trim(),
        quantity: Number(l.quantity) || 0,
        unit_price: Number(l.unit_price) || 0,
        taxable: isRet ? false : l.taxable !== false,
        income_account_id: l.income_account_id || defaultIncome,
        item_id: l.item_id || null,
        expense_id: l.expense_id || null,
      })),
    };
  }

  async function save(finalize) {
    if (!form.customer_id) { setFormError('Choose a customer.'); return; }
    const clean = lines.filter((l) => l.description.trim());
    if (clean.length === 0) { setFormError('Add at least one line item.'); return; }
    setSaving(true);
    setFormError(null);
    const body = buildInvoiceBody(finalize);
    try {
      if (mode === 'edit' && current) {
        await api(`/invoices/${current.id}`, { method: 'PUT', body });
      } else {
        await api('/invoices', { method: 'POST', body });
      }
      setOpen(false);
      loadList();
    } catch (e) {
      setFormError(e?.detail || 'Could not save the invoice.');
    } finally {
      setSaving(false);
    }
  }

  async function finalizeExisting() {
    if (!current) return;
    setSaving(true);
    setFormError(null);
    try {
      await api(`/invoices/${current.id}/finalize`, { method: 'POST' });
      setOpen(false);
      loadList();
    } catch (e) {
      setFormError(e?.detail || 'Could not finalize.');
    } finally {
      setSaving(false);
    }
  }

  function startPayment() {
    const defaultDeposit = depositAccounts.find((a) => a.code === '1010') || depositAccounts[0];
    setPayForm({
      amount: Number(current.balance) || 0,
      payment_date: today(),
      method: 'check',
      reference: '',
      deposit_account_id: defaultDeposit ? defaultDeposit.id : '',
      notes: '',
    });
    setPayError(null);
    setPaying(true);
  }

  async function reloadCurrent() {
    if (!current) return;
    try {
      const inv = await api(`/invoices/${current.id}`);
      setCurrent(inv);
    } catch { /* keep existing */ }
  }

  async function recordPayment() {
    const amt = Number(payForm.amount);
    if (!(amt > 0)) { setPayError('Enter an amount greater than zero.'); return; }
    if (amt > Number(current.balance) + 0.001) { setPayError('Amount exceeds the balance due.'); return; }
    setPaySaving(true);
    setPayError(null);
    try {
      await api('/payments', {
        method: 'POST',
        body: {
          business_id: activeId,
          customer_id: current.customer_id,
          payment_date: payForm.payment_date,
          method: payForm.method,
          reference: payForm.reference || null,
          deposit_account_id: payForm.deposit_account_id || null,
          notes: payForm.notes || null,
          allocations: [{ invoice_id: current.id, amount: amt }],
        },
      });
      setPaying(false);
      await reloadCurrent();
      loadList();
    } catch (e) {
      setPayError(e?.detail || 'Could not record the payment.');
    } finally {
      setPaySaving(false);
    }
  }

  const visibleRows = useMemo(() => {
    if (statusFilter === 'all') return rows;
    if (statusFilter === 'active') return rows.filter((r) => r.status !== 'void');
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  const columns = [
    { key: 'invoice_number', header: 'Number', render: (r) => (
      <span>{r.invoice_number}{r.kind === 'retainer' && <span className="tag tag--retainer">Retainer</span>}</span>
    ) },
    { key: 'customer', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'issue_date', header: 'Issued', render: (r) => date(r.issue_date) },
    { key: 'due_date', header: 'Due', render: (r) => date(r.due_date) },
    { key: 'total', header: 'Total', render: (r) => <span className="num">{money(r.total)}</span> },
    { key: 'balance', header: 'Balance', render: (r) => <span className="num">{money(r.balance)}</span> },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'opened', header: 'Opened', render: (r) => {
      const e = r.email_open;
      if (!e) return <span className="hint">—</span>;
      if (e.first_clicked_at) return <span title={`Clicked the link · opened ${e.open_count}×`} style={{ color: '#16a34a', fontWeight: 600 }}>● clicked</span>;
      if (e.first_opened_at) return <span title={`Opened ${e.open_count}×`} style={{ color: '#b8860b', fontWeight: 600 }}>● opened</span>;
      return <span className="hint" title="Sent — not opened yet">sent</span>;
    } },
  ];

  const readOnly = mode === 'view';
  const isRetainer = form?.kind === 'retainer';
  const canSend = current && current.status !== 'draft' && current.status !== 'void';
  const title = mode === 'new' ? (isRetainer ? 'New retainer' : 'New invoice')
    : current ? `${current.kind === 'retainer' ? 'Retainer' : 'Invoice'} ${current.invoice_number}` : 'Invoice';

  const canVoid = current && current.status !== 'void' && can('invoices.finalize');
  const footer = readOnly ? (
    <>
      <button className="btn" onClick={() => setOpen(false)}>Close</button>
      {current && (
        <button className="btn" onClick={downloadPdf} disabled={pdfBusy}>
          {pdfBusy ? 'Preparing…' : 'Download PDF'}
        </button>
      )}
      {canVoid && (
        <button className="btn btn--danger" onClick={voidInvoice} disabled={sending}>Void</button>
      )}
      {current && current.kind !== 'retainer' && retainer && retainer.available > 0 && Number(current.balance) > 0 && can('invoices.create') && (
        <button className="btn" onClick={applyRetainer} disabled={applying}>
          {applying ? 'Applying…' : `Apply retainer (${money(Math.min(retainer.available, Number(current.balance)))})`}
        </button>
      )}
      {canSend && can('invoices.create') && (
        <button className="btn btn--primary" onClick={sendInvoice} disabled={sending}>
          {sending ? 'Sending…' : current.sent_at ? 'Resend to customer' : 'Send to customer'}
        </button>
      )}
    </>
  ) : (
    <>
      <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
      {canVoid && (
        <button className="btn btn--danger" onClick={voidInvoice} disabled={saving || sending}>Void</button>
      )}
      <button className="btn" onClick={() => save(false)} disabled={saving}>
        {saving ? 'Saving…' : 'Save draft'}
      </button>
      {can('invoices.finalize') && (
        <button className="btn btn--primary" onClick={() => save(true)} disabled={saving}>
          Save &amp; finalize
        </button>
      )}
    </>
  );

  return (
    <>
      <PageHeader title="Invoices" actions={can('invoices.create') && (
        <>
          <button className="btn" onClick={() => openNew('retainer')}>New retainer</button>
          <button className="btn btn--primary" onClick={() => openNew('standard')}>New invoice</button>
        </>
      )} />
      <section className="panel">
        <div className="list-toolbar">
          <label className="list-toolbar__filter">
            <span>Status</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="active">Active (hide voided)</option>
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="open">Open</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="void">Void</option>
            </select>
          </label>
        </div>
        <DataTable
          columns={columns}
          rows={visibleRows}
          loading={loading}
          error={error}
          getRowKey={(r) => r.id}
          onRowClick={openRow}
          empty={statusFilter === 'active' ? 'No active invoices. Choose “All” to include voided ones.' : 'No invoices match this filter.'}
        />
      </section>

      <Drawer open={open} title={title} width={720} onClose={() => !saving && setOpen(false)} footer={form && footer}>
        {form && (
          <>
            {formError && <div className="form-error">{formError}</div>}

            {isRetainer && !readOnly && (
              <div className="form-note" style={{ marginBottom: 14 }}>
                This is a deposit/retainer. It posts to Deferred Revenue (not income) and isn’t taxed — apply it to a future invoice when the work is billed.
              </div>
            )}

            {readOnly && current && (
              <div className="inv-status-row">
                <StatusBadge status={current.status} />
                {current.finalized_at && <span className="hint">Finalized {date(current.finalized_at)}</span>}
                {current.sent_at && <span className="hint">· Sent {date(current.sent_at)}{current.last_sent_to ? ` to ${current.last_sent_to}` : ''}</span>}
                {current.email_open && current.email_open.first_clicked_at && (
                  <span className="badge badge--green" title={`Clicked ${current.email_open.click_count}× · last ${date(current.email_open.last_clicked_at)}`}>● Clicked link</span>
                )}
                {current.email_open && (current.email_open.first_opened_at
                  ? <span className="badge badge--slate" title={`Opened ${current.email_open.open_count}× · last ${date(current.email_open.last_opened_at)}`}>● Opened</span>
                  : <span className="hint">· not opened yet</span>)}
              </div>
            )}
            {sendMsg && <div className={sendMsg.ok ? 'form-note' : 'form-error'}>{sendMsg.text}</div>}

            {current && current.kind !== 'retainer' && retainer && retainer.available > 0 && Number(current.balance) > 0 && (
              <div className="retainer-banner">
                <span><strong>{money(retainer.available)}</strong> retainer credit available for {current.customer_name || 'this customer'}.</span>
                {can('invoices.create') && (
                  <button className="btn btn--sm btn--primary" onClick={applyRetainer} disabled={applying}>
                    {applying ? 'Applying…'
                      : current.status === 'draft'
                        ? `Finalize & apply ${money(Math.min(retainer.available, Number(current.balance)))}`
                        : `Apply ${money(Math.min(retainer.available, Number(current.balance)))} to this invoice`}
                  </button>
                )}
              </div>
            )}

            {/* Header fields */}
            {readOnly ? (
              <div className="inv-parties">
                <div>
                  <div className="inv-parties__label">Bill to</div>
                  <div className="inv-parties__name">{current.customer_name}</div>
                  {addrLines(current.billing_address).map((l, i) => <div key={i} className="inv-parties__line">{l}</div>)}
                </div>
                <div>
                  <div className="inv-parties__label">Ship to</div>
                  {(() => {
                    const ship = current.shipping_same ? current.billing_address : current.shipping_address;
                    const ls = addrLines(ship);
                    return ls.length ? ls.map((l, i) => <div key={i} className="inv-parties__line">{l}</div>)
                      : <div className="inv-parties__line">—</div>;
                  })()}
                </div>
              </div>
            ) : (
              <div className="field">
                <label>Customer *</label>
                <select value={form.customer_id} onChange={(e) => setField('customer_id', e.target.value)}>
                  <option value="">Select a customer…</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>
                  ))}
                </select>
              </div>
            )}

            {!readOnly && (
              <div className="field">
                <label>Link to job (optional)</label>
                <select value={form.job_id || ''} onChange={(e) => setField('job_id', e.target.value)} disabled={!form.customer_id}>
                  <option value="">— None —</option>
                  {jobsForCustomer.map((j) => <option key={j.id} value={j.id}>{j.job_number} · {j.title || j.type}</option>)}
                </select>
                {!form.customer_id && <span className="hint">Choose a customer to see their jobs.</span>}
              </div>
            )}

            <div className="form-grid">
              <div className="field">
                <label>Issue date</label>
                {readOnly ? <div className="ro-value">{date(form.issue_date)}</div>
                  : <input type="date" value={form.issue_date} onChange={(e) => setField('issue_date', e.target.value)} />}
              </div>
              <div className="field">
                <label>Due date</label>
                {readOnly ? <div className="ro-value">{date(form.due_date)}</div>
                  : <input type="date" value={form.due_date} onChange={(e) => setField('due_date', e.target.value)} />}
              </div>
            </div>

            {readOnly && (
              <div className="field">
                <label>Linked job</label>
                <select value={form.job_id || ''} onChange={(e) => setInvoiceJob(e.target.value)} disabled={!can('invoices.create')}>
                  <option value="">— None —</option>
                  {jobsForCustomer.map((j) => <option key={j.id} value={j.id}>{j.job_number} · {j.title || j.type}</option>)}
                </select>
              </div>
            )}

            {/* Line items */}
            <h3 className="form-section">Line items</h3>
            <div className="li">
              <div className="li__head">
                <span>Item</span><span>Description</span><span>Qty</span><span>Unit price</span><span>Tax</span><span className="li__amt">Amount</span><span />
              </div>
              {lines.map((l, i) => {
                const amt = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
                return (
                  <div className="li__row" key={i}>
                    {readOnly ? (
                      <>
                        <span className="li__item">{(items.find((x) => x.id === l.item_id) || {}).name || '—'}</span>
                        <span className="li__desc">{l.description}</span>
                        <span>{Number(l.quantity)}</span>
                        <span className="num">{money(l.unit_price)}</span>
                        <span>{l.taxable !== false ? 'Yes' : '—'}</span>
                        <span className="num li__amt">{money(amt)}</span>
                        <span />
                      </>
                    ) : (
                      <>
                        <select className="li__item-sel" value={l.item_id || ''} onChange={(e) => selectItem(i, e.target.value)}>
                          <option value="">— Custom —</option>
                          {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                        </select>
                        <textarea className="li__desc-input" rows={2} value={l.description} placeholder="Description"
                          onChange={(e) => setLine(i, 'description', e.target.value)} />
                        <input type="number" step="0.01" value={l.quantity}
                          onChange={(e) => setLine(i, 'quantity', e.target.value)} />
                        <input type="number" step="0.01" value={l.unit_price}
                          onChange={(e) => setLine(i, 'unit_price', e.target.value)} />
                        {isRetainer
                          ? <span className="hint" style={{ textAlign: 'center' }}>—</span>
                          : <input type="checkbox" checked={l.taxable !== false}
                              onChange={(e) => setLine(i, 'taxable', e.target.checked)} />}
                        <span className="num li__amt">{money(amt)}</span>
                        <button type="button" className="li__rm" onClick={() => removeLine(i)} aria-label="Remove">×</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {!readOnly && (
              <div className="li__addrow">
                <button type="button" className="btn btn--sm" onClick={addLine}>+ Add line</button>
                {form.customer_id && <button type="button" className="btn btn--sm" onClick={openBillable}>+ Add billable expense</button>}
                {meta?.income_accounts?.length > 1 && (
                  <span className="hint">Income account defaults to Service Revenue</span>
                )}
              </div>
            )}

            {/* Income account per line (only when multiple income accounts exist) */}
            {!readOnly && meta?.income_accounts?.length > 1 && (
              <div className="li-accounts">
                {lines.map((l, i) => (
                  <div className="field" key={i}>
                    <label>Line {i + 1} income account</label>
                    <select value={l.income_account_id || defaultIncome}
                      onChange={(e) => setLine(i, 'income_account_id', Number(e.target.value))}>
                      {meta.income_accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {/* Totals */}
            <div className="totals">
              <div className="totals__row"><span>Subtotal</span><span className="num">{money(totals.subtotal)}</span></div>
              {!isRetainer && <div className="totals__row"><span>Tax{taxRate ? ` (${(taxRate * 100).toFixed(2)}%)` : ''}</span><span className="num">{money(totals.tax)}</span></div>}
              <div className="totals__row totals__row--grand"><span>{isRetainer ? 'Deposit total' : 'Total'}</span><span className="num">{money(totals.total)}</span></div>
            </div>

            {/* Terms */}
            <div className="field" style={{ marginTop: 16 }}>
              <label>Terms / notes</label>
              {readOnly ? (
                can('invoices.create') && current && current.status !== 'void' ? (
                  <>
                    <textarea rows={2} value={form.terms} placeholder="Add notes…" onChange={(e) => setField('terms', e.target.value)} />
                    <div style={{ marginTop: 8 }}>
                      <button className="btn btn--sm" onClick={saveNotes} disabled={notesSaving}>{notesSaving ? 'Saving…' : 'Save notes'}</button>
                    </div>
                  </>
                ) : <div className="ro-value">{form.terms || '—'}</div>
              ) : <textarea rows={2} value={form.terms} onChange={(e) => setField('terms', e.target.value)} />}
            </div>

            {readOnly && current?.status === 'draft' && can('invoices.finalize') && (
              <button className="btn btn--primary" style={{ marginTop: 16 }} onClick={finalizeExisting} disabled={saving}>
                Finalize invoice
              </button>
            )}

            {/* Payments — finalized invoices only */}
            {readOnly && current && current.status !== 'draft' && current.status !== 'void' &&
              ((current.payments && current.payments.length > 0) || ['open', 'partial', 'overdue'].includes(current.status)) && (
              <>
                <h3 className="form-section">Payments</h3>
                {current.payments && current.payments.length > 0 ? (
                  <div className="pay-log">
                    {current.payments.map((p) => (
                      <div className="pay-row" key={p.id}>
                        <span>{date(p.payment_date)}</span>
                        <span className="pay-method">
                          {p.method}{p.reference ? ` · ${p.reference}` : ''}
                        </span>
                        <span className="num">{money(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="comment-empty">No payments recorded yet.</div>
                )}

                {['open', 'partial', 'overdue'].includes(current.status) && can('payments.record') && (
                  <>
                    <div className="pay-balance">
                      <span>Balance due</span><span className="num">{money(current.balance)}</span>
                    </div>
                    {retainer && retainer.available > 0 && Number(current.balance) > 0 && (
                      <div className="pay-retainer">
                        {!paying && payError && <div className="form-error">{payError}</div>}
                        <div className="pay-retainer__head">
                          <span>Retainer credit available</span><span className="num">{money(retainer.available)}</span>
                        </div>
                        <div className="pay-retainer__row">
                          <input type="number" step="0.01" value={retainerAmt}
                            placeholder={money(Math.min(retainer.available, Number(current.balance)))}
                            onChange={(e) => setRetainerAmt(e.target.value)} />
                          <button className="btn btn--sm" onClick={applyRetainerAmount} disabled={applying}>
                            {applying ? 'Applying…' : 'Apply to invoice'}
                          </button>
                        </div>
                        <span className="hint">Applies the customer’s deposit toward this invoice. Leave blank to apply the full available amount (up to the balance).</span>
                      </div>
                    )}
                    {!paying ? (
                      <button className="btn btn--sm btn--primary" onClick={startPayment}>Record payment</button>
                    ) : payForm && (
                      <div className="pay-form">
                        {payError && <div className="form-error">{payError}</div>}
                        <div className="form-grid">
                          <div className="field">
                            <label>Amount</label>
                            <input type="number" step="0.01" value={payForm.amount}
                              onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} />
                          </div>
                          <div className="field">
                            <label>Date</label>
                            <input type="date" value={payForm.payment_date}
                              onChange={(e) => setPayForm((f) => ({ ...f, payment_date: e.target.value }))} />
                          </div>
                          <div className="field">
                            <label>Method</label>
                            <select value={payForm.method}
                              onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))}>
                              <option value="check">Check</option>
                              <option value="ach">ACH</option>
                              <option value="wire">Wire</option>
                              <option value="cash">Cash</option>
                              <option value="stripe">Stripe</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div className="field">
                            <label>Reference</label>
                            <input value={payForm.reference} placeholder="Check #, confirmation…"
                              onChange={(e) => setPayForm((f) => ({ ...f, reference: e.target.value }))} />
                          </div>
                          <div className="field">
                            <label>Deposit to</label>
                            <select value={payForm.deposit_account_id}
                              onChange={(e) => setPayForm((f) => ({ ...f, deposit_account_id: e.target.value }))}>
                              {depositAccounts.map((a) => (
                                <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="field">
                          <label>Notes</label>
                          <input value={payForm.notes}
                            onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))} />
                        </div>
                        <div className="pay-actions">
                          <button className="btn btn--sm" onClick={() => setPaying(false)} disabled={paySaving}>Cancel</button>
                          <button className="btn btn--sm btn--primary" onClick={recordPayment} disabled={paySaving}>
                            {paySaving ? 'Recording…' : 'Record payment'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </Drawer>

      <Modal
        open={billableOpen}
        title="Add billable expenses"
        onClose={() => setBillableOpen(false)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setBillableOpen(false)}>Cancel</button>
            <button type="button" className="btn btn--primary" onClick={addBillable} disabled={billableSel.size === 0}>
              Add {billableSel.size || ''} to invoice
            </button>
          </>
        }
      >
        {billableLoading ? (
          <p className="hint">Loading…</p>
        ) : billableList.length === 0 ? (
          <p className="hint">No unbilled billable expenses for this customer. Mark an expense as billable to this customer on the Expenses page first.</p>
        ) : (
          <div className="billable-list">
            {billableList.map((e) => (
              <label className="billable-row" key={e.id}>
                <input type="checkbox" checked={billableSel.has(String(e.id))} onChange={() => toggleBillable(e.id)} />
                <span className="billable-row__vendor">{e.vendor || 'Expense'}</span>
                <span className="billable-row__date">{date(e.expense_date)}</span>
                <span className="num billable-row__amt">{money(e.total)}</span>
              </label>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
